import { getAccessToken, getRefreshToken } from "./authStorage";

const CHANNEL_NAME = "auth-session-sync";

export const SESSION_SYNC_TYPES = {
  REQUEST: "SESSION_REQUEST",
  PAYLOAD: "SESSION_PAYLOAD",
  UPDATE: "SESSION_UPDATE",
  CLEARED: "SESSION_CLEARED",
};

let sharedChannel = null;
let registeredTabId = null;

/** Called from App so login flows can broadcast without passing tabId. */
export function registerAuthTabId(tabId) {
  registeredTabId = tabId;
}

function resolveTabId(tabId) {
  return tabId ?? registeredTabId;
}

function getChannel() {
  if (sharedChannel) {
    return sharedChannel;
  }
  try {
    sharedChannel = new BroadcastChannel(CHANNEL_NAME);
    return sharedChannel;
  } catch {
    return null;
  }
}

function postMessage(data) {
  const ch = getChannel();
  if (ch) {
    ch.postMessage(data);
  }
}

/**
 * @param {number|string} tabId
 * @param {{ onRemoteSession?: (payload: { accessToken: string|null, refreshToken: string|null }) => void, onRemoteCleared?: () => void }} handlers
 * @returns {() => void} cleanup
 */
export function initAuthSessionSync(tabId, { onRemoteSession, onRemoteCleared }) {
  const ch = getChannel();
  if (!ch) {
    return () => {};
  }

  const handler = (event) => {
    const data = event.data || {};
    const { type, fromTabId, accessToken, refreshToken } = data;
    if (fromTabId === tabId) {
      return;
    }

    if (type === SESSION_SYNC_TYPES.REQUEST) {
      const access = getAccessToken();
      const refresh = getRefreshToken();
      if (access && refresh) {
        postMessage({
          type: SESSION_SYNC_TYPES.PAYLOAD,
          fromTabId: tabId,
          accessToken: access,
          refreshToken: refresh,
          targetTabId: fromTabId,
        });
      }
      return;
    }

    if (
      type === SESSION_SYNC_TYPES.PAYLOAD ||
      type === SESSION_SYNC_TYPES.UPDATE
    ) {
      if (data.targetTabId && data.targetTabId !== tabId) {
        return;
      }
      onRemoteSession?.({ accessToken: accessToken ?? null, refreshToken: refreshToken ?? null });
      return;
    }

    if (type === SESSION_SYNC_TYPES.CLEARED) {
      onRemoteCleared?.();
    }
  };

  ch.addEventListener("message", handler);
  return () => ch.removeEventListener("message", handler);
}

/**
 * @param {number|string} tabId
 * @param {{ accessToken?: string|null, refreshToken?: string|null }} payload
 */
export function broadcastSessionPayload(tabId, { accessToken, refreshToken }) {
  const fromTabId = resolveTabId(tabId);
  if (fromTabId == null) {
    return;
  }
  postMessage({
    type: SESSION_SYNC_TYPES.UPDATE,
    fromTabId,
    accessToken: accessToken ?? null,
    refreshToken: refreshToken ?? null,
  });
}

export function broadcastSessionCleared(tabId) {
  const fromTabId = resolveTabId(tabId);
  if (fromTabId == null) {
    return;
  }
  postMessage({
    type: SESSION_SYNC_TYPES.CLEARED,
    fromTabId,
  });
}

function waitForSessionPayload(tabId, timeoutMs) {
  return new Promise((resolve) => {
    const ch = getChannel();
    if (!ch) {
      resolve(null);
      return;
    }

    let settled = false;
    const finish = (value) => {
      if (settled) {
        return;
      }
      settled = true;
      ch.removeEventListener("message", onMessage);
      clearTimeout(timer);
      resolve(value);
    };

    const onMessage = (event) => {
      const data = event.data || {};
      if (data.fromTabId === tabId) {
        return;
      }
      if (
        data.type === SESSION_SYNC_TYPES.PAYLOAD ||
        data.type === SESSION_SYNC_TYPES.UPDATE
      ) {
        if (data.targetTabId && data.targetTabId !== tabId) {
          return;
        }
        if (data.accessToken && data.refreshToken) {
          finish({
            accessToken: data.accessToken,
            refreshToken: data.refreshToken,
          });
        }
      }
    };

    const timer = setTimeout(() => finish(null), timeoutMs);
    ch.addEventListener("message", onMessage);
  });
}

/**
 * Ask other tabs for credentials (new tab bootstrap).
 * @param {number|string} tabId
 * @param {number} [timeoutMs=500]
 * @returns {Promise<{ accessToken: string, refreshToken: string }|null>}
 */
export async function requestSessionFromPeers(tabId, timeoutMs = 500) {
  const ch = getChannel();
  if (!ch) {
    return null;
  }

  postMessage({
    type: SESSION_SYNC_TYPES.REQUEST,
    fromTabId: tabId,
  });

  let payload = await waitForSessionPayload(tabId, timeoutMs);
  if (payload) {
    return payload;
  }

  postMessage({
    type: SESSION_SYNC_TYPES.REQUEST,
    fromTabId: tabId,
  });
  payload = await waitForSessionPayload(tabId, 300);
  return payload;
}
